/**
 * URL utility functions for safe URL construction
 * These functions replace unsafe string concatenation with proper URL construction
 */

/**
 * Creates a URL safely using the URL constructor.
 *
 * Features:
 * - Accepts base URL
 * - Optional path (string or string[])
 * - Joins array paths with `/`
 * - Collapses duplicate slashes
 * - Merges query params with existing ones
 * - New params override existing keys
 * - Optional `removeTrailingSlash` to strip the trailing slash before appending query params
 *
 * @example
 * createUrl("https://api.com")
 * createUrl("https://api.com", { path: "users" })
 * createUrl("https://api.com", { path: ["users", "123"] })
 * createUrl("https://api.com?lang=en", {
 *   path: ["users", "123"],
 *   params: { page: "1" }
 * })
 */
export function createUrl(
	baseUrl: string,
	options?: {
		path?: string | string[]
		params?: Record<string, string | string[] | undefined>
		removeTrailingSlash?: boolean
	},
): string {
	const { path, params, removeTrailingSlash = false } = options ?? {}

	const base = new URL(baseUrl)

	const ALLOWED_PROTOCOLS = ['http:', 'https:']
	if (!ALLOWED_PROTOCOLS.includes(base.protocol)) {
		throw new Error(`Unsafe URL protocol: ${base.protocol}`)
	}

	const existingParams = new URLSearchParams(base.search)
	let finalPath: string | undefined

	if (Array.isArray(path)) {
		finalPath = path
			.map(segment => segment.replace(/^\/+|\/+$/g, '')) // trim slashes
			.filter(Boolean) // remove empty segments
			.map(segment =>
				segment
					.replace(/\/{2,}/g, '/')
					.split('/')
					.map(encodeURIComponent)
					.join('/'),
			)
			.join('/')
	} else if (typeof path === 'string') {
		finalPath = path
			.replace(/\/{2,}/g, '/')
			.split('/')
			.map(encodeURIComponent)
			.join('/')
	}

	if (finalPath) {
		const stripped = finalPath.replace(/^\/+|\/+$/g, '')
		finalPath = removeTrailingSlash ? `/${stripped}` : `/${stripped}/`
		base.pathname = finalPath
	}

	// Reapply existing params and merge new params (override existing ones)
	base.search = existingParams.toString()
	if (params) {
		Object.entries(params).forEach(([key, value]) => {
			if (Array.isArray(value)) {
				value.forEach(item => {
					base.searchParams.append(key, item)
				})
			} else if (value !== undefined) {
				base.searchParams.set(key, value)
			}
		})
	}

	return base.toString()
}

/**
 * Creates a normalized pathname (optionally with query parameters) by joining
 * path sections with `/`, handling root slashes and always appending a trailing slash.
 *
 * Path behavior:
 * - If the first section is exactly "/", the path starts with a single leading slash.
 * - All other sections are trimmed of leading/trailing slashes and URI-encoded.
 * - Sections are joined with `/` and a trailing slash is always appended (except for the root path, which remains "/").
 * - Prevents double slashes at the start of the path.
 *
 * Query parameter behavior:
 * - If `params` is provided, the query string is appended after the trailing slash.
 * - The query string is produced by `parseQueryParamsToString(params)`, which returns a string beginning with `?`.
 *
 * Note: This function is intended for **pathnames only**, not full URLs.
 * Using it with URLs like `https://example.com` would incorrectly become
 * `https:/example.com`.
 *
 * @example
 * generatePath(["/", "users", "123"])              // "/users/123/"
 * generatePath(["users", "username"], { q: "x" })  // "users/username/?q=x"
 * generatePath(["/"])                              // "/"
 */
export function generatePath(
	sections: string[],
	params?: Record<string, string>,
): string {
	const encodeSegment = (segment: string) =>
		segment
			.replace(/^\/+|\/+$/g, '')
			.replace(/\/{2,}/g, '/')
			.split('/')
			.map(encodeURIComponent)
			.join('/')

	let path =
		sections[0] === '/'
			? '/' + sections.slice(1).map(encodeSegment).filter(Boolean).join('/')
			: sections.map(encodeSegment).filter(Boolean).join('/')

	const finalPath = path === '/' ? '/' : `${path}/`

	if (!params || Object.keys(params).length === 0) {
		return finalPath
	}

	const parseQueryParamsToString = (params: Record<string, string>) => {
		const searchParams = new URLSearchParams(params)
		return `?${searchParams.toString()}`
	}
	return `${finalPath}${parseQueryParamsToString(params)}`
}
